/**
 * Simple Member Management Dialog
 * Direct member management without nested dialogs
 * Migrated to BaseDialog pattern (Phase 3.2)
 */

import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { BaseDialog } from '../ui/BaseDialog';
import { useTranslation } from 'react-i18next';
import { useGroupMembers, useAddGroupMember, useRemoveGroupMember } from '../../hooks/api/useGroups';
import { UserSelectionDialog } from './UserSelectionDialog';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { log } from '../../utils/errors/logger';

interface Group {
  id: string;
  displayName?: string;
  source: string;
}

interface SimpleMemberManagementDialogProps {
  group: Group | null;
  open: boolean;
  onClose: () => void;
}

export const SimpleMemberManagementDialog: React.FC<SimpleMemberManagementDialogProps> = ({
  group,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [userSelectionOpen, setUserSelectionOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Fetch members
  const { data: membersResponse, isLoading, refetch } = useGroupMembers(group?.id || '', open && Boolean(group));
  const members = membersResponse?.members || [];

  // Mutations
  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();

  if (!group) {return null;}

  const isLocalGroup = group.source === 'local';
  // Extract existing member IDs for exclusion
  const existingMemberIds = members.map(member => member.user_id || member.id);

  const handleAddMembers = () => {
    setActionError(null);
    setUserSelectionOpen(true);
    log.user.action('open_add_members_dialog', 'SimpleMemberManagementDialog', { groupId: group.id });
  };

  const handleConfirmAddMembers = async (selectedUsers: Array<{ userId: string; role: string }>) => {
    try {
      setActionError(null);
      const promises = selectedUsers.map(({ userId, role }) =>
        addMemberMutation.mutateAsync({ groupId: group.id, userId, role })
      );

      await Promise.all(promises);

      showSuccess(t('groups.members.addSuccess', { count: selectedUsers.length }));
      setUserSelectionOpen(false);
      refetch();
    } catch (error) {
      const errorMessage = t('groups.members.addError');
      setActionError(errorMessage);
      showError(errorMessage);
      log.error('Failed to add members', { error, groupId: group.id });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      setActionError(null);
      await removeMemberMutation.mutateAsync({ groupId: group.id, userId });
      showSuccess(t('groups.members.removeSuccess'));
      refetch();
    } catch (error) {
      const errorMessage = t('groups.members.removeError');
      setActionError(errorMessage);
      showError(errorMessage);
      log.error('Failed to remove member', { error, groupId: group.id, userId });
    }
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={t('groups.members.title', { groupName: group.displayName })}
        maxWidth="sm"
        fullWidth
        loading={false}
        error={actionError}
        actions={
          <>
            {isLocalGroup && (
              <Button
                onClick={handleAddMembers}
                startIcon={<PersonAddIcon />}
                variant="contained"
                disabled={isLoading}
              >
                {t('groups.members.addMembers')}
              </Button>
            )}
            <Button onClick={onClose}>
              {t('common.close')}
            </Button>
          </>
        }
      >
        {!isLocalGroup && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('groups.details.externalGroupInfo', { source: group.source.toUpperCase() })}
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : members.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              {t('groups.details.noMembers')}
            </Typography>
          </Box>
        ) : (
          <List>
            {members.map((member) => {
              const memberId = member.user_id || member.id;
              return (
                <ListItem
                  key={memberId}
                  secondaryAction={
                    isLocalGroup && (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveMember(memberId)}
                        size="small"
                        color="error"
                        disabled={removeMemberMutation.isPending}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle2">
                          {member.full_name || member.username || memberId}
                        </Typography>
                        <Chip
                          size="small"
                          label={member.role}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={member.email}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </BaseDialog>

      <UserSelectionDialog
        open={userSelectionOpen}
        onClose={() => setUserSelectionOpen(false)}
        onConfirm={handleConfirmAddMembers}
        excludeUserIds={existingMemberIds}
        groupName={group.displayName}
        isLoading={addMemberMutation.isPending}
      />
    </>
  );
};